import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useUserTheme } from "../hooks/useUserTheme";
import CategoriesPage from "../modules/settings/categories/CategoriesPage";
import AddProduct from "../modules/settings/AddProduct";

type CatalogTab = "categories" | "products";

interface TabItem {
  id: CatalogTab;
  label: string;
}

const Catalog: React.FC = () => {
  const { bgClass, fontClass, sidebarThemeClass } = useUserTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Retrieve tab from URL parameter securely
  const tabParam = searchParams.get("tab") as CatalogTab;
  const initialTab: CatalogTab =
    tabParam && ["categories", "products"].includes(tabParam)
      ? tabParam
      : "categories";

  const [activeTab, setActiveTab] = useState<CatalogTab>(initialTab);

  const tabs: TabItem[] = [
    { id: "categories", label: "Categories" },
    { id: "products", label: "Products" },
  ];

  // Sync internal state when URL searchParams change (e.g. Browser Back/Forward buttons)
  useEffect(() => {
    if (tabParam && ["categories", "products"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  // Update URL when tab changes
  const handleTabChange = (tabId: CatalogTab) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  return (
    <div className={`w-full shadow-inner rounded-lg ${sidebarThemeClass} flex flex-col h-fit bg-gray-50`}>
      {/* Header Container */}
      <div className="p-2 border-b border-gray-200">
        <div className={`${sidebarThemeClass} p-1.5 rounded-2xl w-full sm:w-fit shadow-sm border border-gray-200`}>
          <nav className="flex gap-2" role="tablist" aria-label="Catalog Tabs">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex-1 sm:flex-initial px-5 py-2.5 text-sm sm:text-base font-medium rounded-xl transition-all duration-150 text-center ${fontClass} ${
                    isActive
                      ? `${bgClass} text-white shadow-sm`
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/60"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main Tab Content */}
      <div className="flex-1 w-full overflow-auto">
        {activeTab === "categories" ? (
          <CategoriesPage showEditMenu={false} />
        ) : (
          <AddProduct showEditMenu={false} />
        )}
      </div>
    </div>
  );
};

export default Catalog;